/**
 * Call-slot validation + presentation (LG-118 Slice 4).
 *
 * Pure and client-safe: the same helpers format the slots injected into the
 * Customer.io outreach email and the ones listed on the in-app "pick a time"
 * surface, so an applicant can never be shown two different renderings of
 * the same offer.
 *
 * Slots travel as ISO-8601 UTC strings end to end. They are only ever turned
 * into human-readable text at the edges, with an explicit timezone label —
 * an unlabeled "2:00" in an email is how people show up an hour late.
 */
import { z } from 'zod'

/**
 * 3–10 offered times. The floor keeps the email from reading like an
 * ultimatum; the ceiling keeps it scannable (and the radio list short).
 */
export const slotsSchema = z.array(z.string().datetime()).min(3).max(10)

/** Timezone the program schedules in, and how it is labeled to applicants. */
export const PROGRAM_TIMEZONE = 'America/New_York'
export const PROGRAM_TZ_LABEL = 'ET'

/** Checkpoint §8.6 default — real duration needs Mike + Jon sign-off. */
export const CALL_DURATION_MINUTES = 30

/**
 * What the intro call is called, wherever it is written down. Shared so the
 * Google Calendar event (server) and the .ics fallback (client) cannot
 * describe the same meeting differently.
 */
export function callSummary(firstName?: string): string {
  return firstName
    ? `Beyond CTCI — intro call with ${firstName}`
    : 'Beyond CTCI — intro call'
}

export const CALL_DESCRIPTION =
  'A short call to talk through your goals and whether the program is a fit.'

/**
 * "Wed, Jul 29, 2:00 PM ET". The label is appended rather than derived from
 * `timeZoneName` because Intl renders "EDT"/"EST" — technically correct, but
 * the seasonal split reads as two different timezones to a lay reader.
 */
export function formatSlot(
  iso: string,
  // Defaulted: every caller passed these two constants straight back from
  // this module, which is a parameter no caller can answer better.
  timeZone: string = PROGRAM_TIMEZONE,
  tzLabel: string = PROGRAM_TZ_LABEL,
): string {
  const formatted = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso))
  return `${formatted} ${tzLabel}`
}

export function formatSlots(
  isos: string[],
  timeZone: string = PROGRAM_TIMEZONE,
  tzLabel: string = PROGRAM_TZ_LABEL,
): string[] {
  return isos.map((iso) => formatSlot(iso, timeZone, tzLabel))
}

const DAY_MS = 86_400_000
/**
 * The furthest any inhabited place runs ahead of UTC (UTC+14, Kiritimati).
 * A calendar day has begun *somewhere* this long before it begins in UTC.
 */
const MAX_UTC_OFFSET_MS = 14 * 60 * 60 * 1000

/**
 * Built once for the process, unlike `formatSlot`'s: constructing an
 * `Intl.DateTimeFormat` is among the most expensive things in the standard
 * library, and every option here is a constant — there is nothing per-call to
 * parameterize it with.
 */
const reapplyDateFormat = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  year: 'numeric',
  month: 'long',
  day: 'numeric',
})

/**
 * "November 27, 2026" — the day a declined applicant may apply again, as the
 * rejection email prints it.
 *
 * Here rather than in the Customer.io template because Liquid has no
 * timezone-safe date arithmetic, and beside `formatSlot` because it is the
 * same job: render a funnel date for an email, once, so the applicant and the
 * app can never be looking at two different renderings of one fact.
 *
 * Unlike `formatSlot` this names a **day, not a moment**, so it carries no
 * timezone label — and that is exactly what makes it delicate. `expiresAt` is
 * an instant, and `isCooldownActive` refuses while it is still in the future,
 * so the calendar day containing it is partly a day the applicant is still
 * turned away. Naming that day sends them to a form that rejects them, which
 * is the drift this function exists to prevent.
 *
 * So: advance to the first UTC midnight that has not yet arrived anywhere on
 * earth when the cooldown lifts. Rounding to the next UTC midnight alone is
 * not enough — UTC+14 starts its day fourteen hours early, so a date safe in
 * UTC can still be a refused morning in Kiribati. Costs at most one extra day
 * against a 90-day block, and buys a date that is true for every reader.
 */
export function formatReapplyDate(expiresAt: Date): string {
  const firstFullyOpenDay = new Date(
    Math.ceil((expiresAt.getTime() + MAX_UTC_OFFSET_MS) / DAY_MS) * DAY_MS,
  )
  return reapplyDateFormat.format(firstFullyOpenDay)
}
